import React, { FC, useRef, useState } from 'react';
import { getNCFilesByProductionOrderId } from '@reducers/manufacture';
import { updateHeaderConfig } from '@reducers/details';
import { connect } from 'react-redux';
import { getProductionBatchByProductionOrderId, productionOrdersSearch } from '@reducers/search';
import { OperationSearchItem, ProductionOrderSearchItem } from '@interfaces/search';
import { ReducerEntityWithLoadingState } from '@v2/pages/common/interfaces';
import { ProductionOrderScreenProps } from '@v2/pages/productionOrder/common/interfaces';
import { useProductionOrderDetails } from '@v2/pages/common/hooks/useProductionOrderDetails';
import { loadSingleBatch } from '@reducers/batch';
import { ALL_JOBS_ID, INFO_SCREEN_LIST_WIDTH, SCREEN_PADDING_HORIZONTAL_FOR_PAGE } from '@v2/pages/common/constants/base.constants';
import { fullWidth, scale, width } from '@components/frame';
import { isPhone, isWeb, parseRouteParamAsNumber } from '@helpers';
import ProductionOrderContentWrapper from './components/ProductionOrderContentWrapper';
import { useRoute } from '@react-navigation/native';
import ColorWrapper from '@styles/colors';
import { ColorFunction } from '@styles/colors/types';
import ProductionOrderContentDifferentModes from './components/ProductionOrderContentDifferentModes';
import { PaginatedResponse } from '@interfaces/machines';
import { LevelSubScreen } from '@v2/pages/common/enums';
import { useHandleNavigationContextHeader } from '@v2/pages/common/hooks/useHandleNavigationContextHeader';

interface ProductionOrderDetailsProductionOrderProps extends ProductionOrderScreenProps {
  productionOrders: ReducerEntityWithLoadingState<ProductionOrderSearchItem[]>;
  C: ColorFunction;
}

const ProductionOrderDetailsProductionOrderComponent: FC<ProductionOrderDetailsProductionOrderProps> = ({
  updateHeaderConfig,
  getNCFilesByProductionOrderId,
  getProductionBatchByProductionOrderId,
  loadSingleBatch,
  productionOrdersSearch,
  productionOrders,
  C,
}) => {
  const route = useRoute();
  const { activeLevelSubScreenName } = useHandleNavigationContextHeader();

  const jobId = parseRouteParamAsNumber(route.params?.selectedProductionOperationId);
  const allJobsSelected = jobId === ALL_JOBS_ID;

  const jobsApiCallRef = useRef<(() => Promise<PaginatedResponse<OperationSearchItem>>) | null>(null);

  const setJobsApiCall = (apiCallFunction: () => Promise<PaginatedResponse<OperationSearchItem>>) => {
    jobsApiCallRef.current = apiCallFunction;
  };

  const [isError, setIsError] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);

  useProductionOrderDetails({
    productionOrdersSearch,
    getNCFilesByProductionOrderId,
    getProductionBatchByProductionOrderId,
    loadSingleBatch,
    updateHeaderConfig,
  });

  const productionOrder = productionOrders.data?.[0];
  const loading = !productionOrders.wasLoaded;

  return (
    <ProductionOrderContentWrapper
      activeLevelSubScreenName={activeLevelSubScreenName as LevelSubScreen}
      loading={loading}
      productionOrder={productionOrder}
      setIsError={setIsError}
      setTotalCount={setTotalCount}
      setJobsApiCall={setJobsApiCall}
    >
      <ProductionOrderContentDifferentModes
        jobsApiCall={jobsApiCallRef.current}
        productionOrder={productionOrder}
        loading={loading}
        maxContentWidth={isPhone ? width : fullWidth - scale(INFO_SCREEN_LIST_WIDTH)}
        isError={isError}
        totalCount={totalCount}
        withoutHeader={allJobsSelected}
        bg={!isWeb ? 'Background/Light/Field on Panel' : 'Background/Light/Field'}
        contentStyle={
          allJobsSelected
            ? {
                paddingTop: scale(32),
                position: 'relative',
                backgroundColor: C(isWeb ? 'Background/Light/System' : 'Background/Light/Panel'),
                height: '100%',
              }
            : {}
        }
      />
    </ProductionOrderContentWrapper>
  );
};

const ProductionOrderDetailsProductionOrder = connect(({ details: { productionOrders } }) => ({ productionOrders }), {
  productionOrdersSearch,
  updateHeaderConfig,
  getNCFilesByProductionOrderId,
  getProductionBatchByProductionOrderId,
  loadSingleBatch,
})(ColorWrapper(ProductionOrderDetailsProductionOrderComponent));

export { ProductionOrderDetailsProductionOrder };
